import { memo, useState } from "react"
import { IconAtom2Outline18 } from "nucleo-ui-outline-18"

import { Button, Empty, Loader } from "@productlane/ui"

import { toast } from "@/components/toast"
import { api } from "@/lib/api"
import { reviewProgressLabel } from "./format"

const POLL_MS = 3_000
// Without Redis there is no status to read, so a started review is assumed to
// run this long unless cards show up first.
const LOCAL_RUNNING_MS = 15 * 60 * 1000

export function useAgentReview() {
	const utils = api.useUtils()
	const [requestedAt, setRequestedAt] = useState<number | null>(null)
	const locallyRunning =
		requestedAt !== null && Date.now() - requestedAt < LOCAL_RUNNING_MS

	const status = api.ai.getAgentRecommendationsReviewStatus.useQuery(
		undefined,
		{
			refetchInterval: (data) =>
				data?.state === "running" || (locallyRunning && !data)
					? POLL_MS
					: false,
			refetchOnWindowFocus: true,
		},
	)

	const start = api.ai.analyzeAgentRecommendations.useMutation({
		onSuccess: async () => {
			setRequestedAt(Date.now())
			await utils.ai.getAgentRecommendationsReviewStatus.invalidate()
		},
		onError: async (error) => {
			// Someone else started one; show theirs instead of an error.
			if (error.data?.code === "CONFLICT") {
				await utils.ai.getAgentRecommendationsReviewStatus.invalidate()
				return
			}
			toast.error({ title: "Couldn't start the review" })
		},
	})

	const data = status.data ?? null
	const running =
		data?.state === "running" ||
		start.isLoading ||
		(locallyRunning && data === null)

	return {
		status: data,
		running,
		starting: start.isLoading,
		start: () => start.mutate({ days: 90 }),
	}
}

type Review = ReturnType<typeof useAgentReview>

export const ReviewIndicator = memo(({ review }: { review: Review }) => (
	<span className="flex items-center gap-2 whitespace-nowrap text-xs text-tertiary">
		<Loader size={4} />
		{reviewProgressLabel(review.status?.read, review.status?.total)}
	</span>
))

export const EmptyRecommendations = memo(
	({ review, className = "mt-16" }: { review: Review; className?: string }) => {
		const { status, running, starting, start } = review

		if (running && !starting) {
			return (
				<Empty
					className={className}
					image={
						<div className="flex h-10 items-center justify-center">
							<Loader size={10} variant="retrieve" />
						</div>
					}
					title={reviewProgressLabel(status?.read, status?.total)}
					text="Suggestions show up here as soon as they're ready."
				/>
			)
		}

		const finishedEmpty =
			status?.state === "done" && (status.created ?? 0) === 0
		const failed = status?.state === "failed"

		return (
			<Empty
				className={className}
				image={<IconAtom2Outline18 className="h-8 w-8 text-secondary" />}
				title={
					failed
						? "The review stopped"
						: finishedEmpty
							? "Nothing to fix right now"
							: "No suggestions yet"
				}
				text={
					failed
						? "Something went wrong while reading conversations. Try again."
						: finishedEmpty
							? "The last review found nothing to fix. The next one runs tomorrow morning."
							: "Every morning the agent reviews the conversations it couldn't finish and suggests fixes here."
				}
				action={
					<Button
						variant="outline"
						isLoading={starting}
						disabled={starting}
						onClick={start}
					>
						{failed
							? "Try again"
							: finishedEmpty
								? "Review again"
								: "Review the last 90 days"}
					</Button>
				}
			/>
		)
	},
)
